import {
  DialogActions,
  DialogContent,
  DialogProps,
  Dialog as MuiDialog,
} from "@mui/material";
import { memo, ReactNode, useEffect, useRef } from "react";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import { DialogTitleWithCloseButton } from "./DialogTitleWithCloseButton";
import { useSupportWidget } from "../../hooks/useSupportWidget";
import OverlayScrollbarWrapper from "../OverlayScrollbar/OverlayScrollbarWrapper";
import { Box } from "../Box";
import { spacing } from "../../../styles/theme";
import { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";

interface Props extends Omit<DialogProps, "content"> {
  open: boolean;
  onClose: () => void;
  title: string;
  content: ReactNode;
  action?: ReactNode;
  scrollToEndOnContentExpand?: boolean;
}

const Dialog = ({
  open,
  onClose,
  title,
  content,
  action,
  scrollToEndOnContentExpand = false,
  ...props
}: Props) => {
  const { isMobile } = useThemeBreakpoints();
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<OverlayScrollbarsComponentRef<"div">>(null);

  const { hideWidget, showWidget } = useSupportWidget();

  useEffect(() => {
    if (isMobile) {
      if (open) {
        hideWidget();
      } else {
        showWidget();
      }
    }
  }, [open, hideWidget, showWidget, isMobile]);

  const instance = scrollbarRef.current?.osInstance();

  useEffect(() => {
    if (!scrollToEndOnContentExpand || !contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      const timeoutId = setTimeout(() => {
        if (instance && contentRef.current) {
          const viewport = instance.elements().viewport;
          viewport.scrollTo({
            top: contentRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [instance, scrollToEndOnContentExpand]);

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={"md"}
      fullScreen={isMobile}
      {...props}
      sx={{
        ...props.sx,
        "& .MuiDialog-paper": {
          p: 0,
        },
      }}
    >
      <OverlayScrollbarWrapper ref={scrollbarRef} key="dialog-scroll">
        <Box
          height={"100%"}
          px={{ xs: spacing.wrapperMobile, lg: spacing.wrapperDesktop }}
          pt={{ xs: spacing.wrapperMobile, lg: spacing.wrapperDesktop }}
        >
          <DialogTitleWithCloseButton
            id="customized-dialog-title"
            onClose={onClose}
          >
            {title}
          </DialogTitleWithCloseButton>
          <DialogContent
            sx={{
              pb: {
                xs: spacing.wrapperMobile,
                lg: !action ? spacing.wrapperDesktop : 0,
              },
            }}
            ref={contentRef}
          >
            {isMobile ? (
              <Box
                display="flex"
                flexDirection="column"
                gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
              >
                {content}
                {isMobile && action}
              </Box>
            ) : (
              content
            )}
          </DialogContent>
          {!isMobile && action && (
            <DialogActions sx={{ pb: spacing.wrapperDesktop }}>
              {action}
            </DialogActions>
          )}
        </Box>
      </OverlayScrollbarWrapper>
    </MuiDialog>
  );
};

export default memo(Dialog);
